/**
 * "Ver como" en SOLO LECTURA, para toda la app y sin tocar portal por portal.
 *
 * El cliente de Supabase (`integrations/supabase/client.ts`) recibe un
 * `global.fetch` envuelto con `crearFetchSoloLectura`. Mientras haya un
 * impersonado publicado en `ImpersonationTargetContext` (el provider prende el
 * flag de este módulo en un efecto), el envoltorio rechaza en el navegador:
 *
 *   - Mutaciones REST a tablas: POST, PATCH, PUT y DELETE a `/rest/v1/<tabla>`.
 *   - Escrituras de Storage: POST, PUT y DELETE a `/storage/v1/object/*` y a
 *     `/storage/v1/bucket*`. Pasan `object/sign/*` y `object/list/*`, que aunque
 *     viajan por POST son lecturas (URL firmada y listado).
 *
 * Lo que PASA siempre:
 *   - Lecturas (GET/HEAD) de todo.
 *   - RPC (`/rest/v1/rpc/*`) y Edge Functions (`/functions/v1/*`): el back las
 *     valida con `auth.uid()`, es decir con la sesión REAL del admin, nunca con
 *     la del impersonado. El front no puede saber si una RPC lee o escribe, y
 *     cada una ya decide con el rol verdadero.
 *   - Auth (`/auth/v1/*`): refrescar el token o cerrar sesión no son datos.
 *
 * La respuesta rechazada imita a PostgREST (403 + `code: 42501`) para que cada
 * pantalla muestre su error normal en lugar de reventar.
 *
 * Las subidas por `XMLHttpRequest` directo a Storage no pasan por este fetch:
 * cada una consulta `isSoloLecturaVerComoActiva()` antes de subir.
 *
 * Funciones PURAS y un flag de módulo: sin React ni Supabase, cubiertas por
 * tests en `__tests__/solo-lectura.test.ts`.
 */

export const MENSAJE_SOLO_LECTURA = 'Solo lectura mientras ves como otro usuario';
export const CODIGO_SOLO_LECTURA = '42501';

let soloLecturaActiva = false;
let nombreVerComo: string | null = null;

/**
 * Lo llama `ImpersonationTargetProvider` cada vez que cambia el impersonado.
 * `nombre` es a quién se está viendo, para que el aviso diga de quién se trata.
 */
export function setSoloLecturaVerComo(activa: boolean, nombre: string | null = null): void {
  soloLecturaActiva = activa;
  nombreVerComo = activa ? nombre : null;
}

/**
 * Detalle del aviso: con un "Ver como" guardado de una visita anterior el admin
 * no sabe por qué no puede guardar; se le dice a quién está viendo y cómo salir.
 */
export function detalleSoloLectura(): string {
  const quien = nombreVerComo ? `Estás viendo como ${nombreVerComo}.` : 'Tienes un "Ver como" activo.';
  return `${quien} Cambia a "Mi vista" en el selector "Ver como" para guardar.`;
}

export function isSoloLecturaVerComoActiva(): boolean {
  return soloLecturaActiva;
}

/**
 * Escrituras REST que SÍ se permiten al ver como otro usuario. Lista mínima y
 * explícita: cada entrada dice por qué no es un cambio de datos del impersonado.
 */
export const LISTA_BLANCA_REST: ReadonlyArray<{ metodo: string; tabla: string; motivo: string }> = [
  {
    metodo: 'POST',
    tabla: 'logs_actividad',
    motivo:
      'Bitácora de auditoría (activityLoggerService): registra vistas y cierre de sesión con el ' +
      'usuario REAL de la sesión. Bloquearla dejaría sin rastro lo que el admin hizo al impersonar.',
  },
];

/**
 * Escrituras REST que se rechazan EN SILENCIO (sin toast). Son marcas de "leído"
 * que la pantalla dispara sola al abrir un chat o una notificación: el admin no
 * hizo nada, así que avisarle sería ruido. Se rechazan igual porque marcarían como
 * leído algo que el usuario impersonado no leyó (y con el uid del admin).
 * `metodos` vacío = cualquier método de escritura (los upsert viajan por POST).
 */
export const LISTA_SILENCIOSA_REST: ReadonlyArray<{ tabla: string; metodos: readonly string[] }> = [
  { tabla: 'chat_cliente_lecturas', metodos: [] },
  { tabla: 'broker_chat_lecturas', metodos: [] },
  { tabla: 'notificaciones_cliente', metodos: ['PATCH'] },
];

const METODOS_ESCRITURA = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);

export type DecisionSoloLectura =
  | { permitir: true }
  | { permitir: false; tipo: 'rest' | 'storage'; recurso: string; silencioso: boolean };

function rutaDe(url: string): string {
  try {
    return new URL(url, 'http://localhost').pathname;
  } catch {
    return url.split('?')[0] ?? url;
  }
}

/** ¿Se deja pasar esta petición en solo lectura? Pura: no mira el flag. */
export function decidirPeticion(url: string, metodo: string | undefined): DecisionSoloLectura {
  const m = (metodo ?? 'GET').toUpperCase();
  if (!METODOS_ESCRITURA.has(m)) return { permitir: true };

  const ruta = rutaDe(url);

  const rest = ruta.match(/\/rest\/v1\/([^/]+)/);
  if (rest) {
    const tabla = decodeURIComponent(rest[1]);
    if (tabla === 'rpc') return { permitir: true };
    if (LISTA_BLANCA_REST.some((w) => w.metodo === m && w.tabla === tabla)) return { permitir: true };
    const silencioso = LISTA_SILENCIOSA_REST.some(
      (w) => w.tabla === tabla && (w.metodos.length === 0 || w.metodos.includes(m)),
    );
    return { permitir: false, tipo: 'rest', recurso: tabla, silencioso };
  }

  const storage = ruta.match(/\/storage\/v1\/(object|bucket)(\/.*)?$/);
  if (storage) {
    const resto = storage[2] ?? '';
    if (storage[1] === 'object' && m === 'POST' && /^\/(sign|list)\//.test(resto)) {
      return { permitir: true };
    }
    return { permitir: false, tipo: 'storage', recurso: `${storage[1]}${resto}`, silencioso: false };
  }

  return { permitir: true };
}

/** Respuesta con la forma de error de PostgREST. */
export function respuestaSoloLectura(): Response {
  return new Response(
    JSON.stringify({ message: MENSAJE_SOLO_LECTURA, code: CODIGO_SOLO_LECTURA, details: null, hint: null }),
    { status: 403, statusText: 'Forbidden', headers: { 'Content-Type': 'application/json' } },
  );
}

type Fetch = typeof fetch;

function urlYMetodo(input: RequestInfo | URL, init?: RequestInit): { url: string; metodo: string | undefined } {
  if (typeof input === 'string') return { url: input, metodo: init?.method };
  if (input instanceof URL) return { url: input.href, metodo: init?.method };
  return { url: input.url, metodo: init?.method ?? input.method };
}

/**
 * Envuelve un fetch. `estaActiva` y `alBloquear` se inyectan para poder
 * probarlo sin React; en la app son el flag de este módulo y un toast.
 * `alBloquear` no se llama para lo de `LISTA_SILENCIOSA_REST`.
 */
export function crearFetchSoloLectura(opciones: {
  base?: Fetch;
  estaActiva?: () => boolean;
  alBloquear?: (decision: Extract<DecisionSoloLectura, { permitir: false }>) => void;
} = {}): Fetch {
  const estaActiva = opciones.estaActiva ?? isSoloLecturaVerComoActiva;
  return (input, init) => {
    // `globalThis.fetch` se resuelve en cada llamada: así los tests (o un polyfill
    // cargado después) lo pueden sustituir.
    const base = opciones.base ?? globalThis.fetch;
    if (!estaActiva()) return base(input, init);
    const { url, metodo } = urlYMetodo(input, init);
    const decision = decidirPeticion(url, metodo);
    if (decision.permitir) return base(input, init);
    // tsconfig sin strictNullChecks: el `if` de arriba no estrecha la unión, se hace a mano.
    const bloqueo = decision as Extract<DecisionSoloLectura, { permitir: false }>;
    if (!bloqueo.silencioso) opciones.alBloquear?.(bloqueo);
    return Promise.resolve(respuestaSoloLectura());
  };
}

/**
 * Limita un aviso a uno por ventana: un guardado que dispara 5 PATCH seguidos
 * no debe apilar 5 toasts.
 */
export function crearAvisoSinRafaga(aviso: () => void, ventanaMs = 4000, ahora: () => number = Date.now) {
  let ultimo = -Infinity;
  return () => {
    const t = ahora();
    if (t - ultimo < ventanaMs) return;
    ultimo = t;
    aviso();
  };
}
